import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { topRuns, type RunRow } from './api';
import { liveList, myLive, claimLiveNow, verifyLiveNow, type LiveRow } from './live';
import { online } from '../../lib/supabase';
import { useAuth } from '../auth/AuthProvider';
import { usePref } from '../../lib/pref';
import { Seg } from '../../components/ui';
import { de } from '../../engine/util';
import { diffLabel } from '../../engine/level12';
import { S as liveS } from '../../engine/state';

const legText = (n: number) => `${n}. Legislatur`;

/** Hinweis für Spieler ohne Konto: mit Anmeldung in die Live-Liste. */
export function LiveSignupHint({ compact }: { compact?: boolean }) {
  const { user } = useAuth();
  if (user || !online()) return null;
  const S = liveS as any, gov = !!S && !S.mp && (S.career?.length > 0 || ['govern', 'review'].includes(S.phase));
  return (
    <div className={'livehint' + (compact ? ' compact' : '')}>
      <b>{gov ? `${S.name} regiert seit ${S.career?.[0]?.a ?? S.legYear ?? 2029}. Soll das jeder sehen?` : 'Deine Regierung in der Live-Liste'}</b>
      <span className="sub">Mit einem Konto erscheint deine laufende Regierung in der Bestenliste unter „Laufende Regierungen“. Dein Spielstand liegt dann zusätzlich sicher in der Cloud. Anmelden geht per E-Mail, ohne Passwort.</span>
      <Link to="/profil" className="btn yes small">Anmelden und mitspielen</Link>
    </div>
  );
}

function LiveTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState<LiveRow[] | null | undefined>(undefined);
  const [mine, setMine] = useState<{ live: any; live_at: string | null; verified: boolean } | null>(null);
  const [why, setWhy] = useState<string | null>(null);
  useEffect(() => {
    let off = false;
    (async () => {
      if (user) { await claimLiveNow(liveS); const r = await verifyLiveNow(); if (!off) setWhy(r); }
      const [l, m] = await Promise.all([liveList(25), user ? myLive() : Promise.resolve(null)]);
      if (!off) { setRows(l); setMine(m); }
    })();
    return () => { off = true; };
  }, [user?.id]);
  const inList = rows?.some((r) => r.mine);
  return <>
    <p className="sub">Wer regiert gerade am längsten? Hier stehen die laufenden Partien angemeldeter Spieler, die in den letzten 14 Tagen gespielt wurden. „Geprüft“ heißt: Der Server hat den Spielstand aus dem Zugprotokoll nachgespielt.</p>
    <LiveSignupHint />
    {user && rows !== undefined && <div className="livehint compact"><b>Deine Regierung</b><span className="sub">{
      mine?.live?.running ? `${mine.live.party}: ${legText(mine.live.leg)}, ${de(mine.live.poll ?? 0)} % in den Umfragen. ${inList ? 'Du stehst in der Liste.' : 'Für die ersten 25 reicht es noch nicht.'} ${mine.verified ? 'Vom Server geprüft.' : (why && /Replay fehlgeschlagen/.test(why) ? `Noch nicht geprüft: Der Server kann deine Partie nicht exakt nachspielen. Sie steht trotzdem in der Liste. (${why})` : `Noch nicht geprüft${why ? ` (${why})` : ''}.`)}`
        : 'Sobald du regierst, erscheint deine Partie hier. Gespeichert wird automatisch, wenn du angemeldet spielst.'}</span></div>}
    {rows === undefined ? <p className="sub">Lade…</p> : rows === null ? <p className="sub">Die Live-Liste ist noch nicht eingerichtet.</p> : !rows.length ? <p className="sub">Gerade regiert niemand. Starte eine Partie mit Konto und sei die erste Regierung auf der Liste.</p> : (
      <div className="lb">{rows.map((r, i) => (
        <div className={'row' + (r.mine ? ' mine' : '')} key={i}><span className="rank">{i + 1}</span>
          <div className="mi"><b>{r.party}{r.verified && <span className="tag good" title="Vom Server nachgespielt und bestätigt" style={{ marginLeft: 8 }}>geprüft</span>}{r.auto && <span className="tag down" title="Die Regierung hat die Demokratie ausgehöhlt" style={{ marginLeft: 8 }}>autoritär</span>}{r.mine && <span className="tag" style={{ marginLeft: 8 }}>du</span>}</b><span className="sub">{r.username || 'Anonym'} · regiert seit {r.since_year} · {diffLabel(r.difficulty)}</span></div>
          <span className="num">{r.poll != null ? de(r.poll) + ' %' : '–'}</span><b className="num">{legText(r.leg)}</b></div>))}</div>)}
  </>;
}

function DoneTab() {
  const [rows, setRows] = useState<RunRow[] | null>(null);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [cat, setCat] = useState<'normal' | 'autoritaer'>('normal');
  const [lv, setLv] = usePref<number>('kabinett-bestenliste-stufe', 0);
  useEffect(() => { setRows(null); topRuns(25, verifiedOnly, cat, lv).then(setRows); }, [verifiedOnly, cat, lv]);
  return <>
    <Seg value={cat} options={[['normal', 'Demokratisch regiert'], ['autoritaer', 'Autoritäre Partien']]} onChange={setCat} />
    {cat === 'autoritaer' && <p className="sub">Partien, in denen die Regierung die Demokratie ausgehöhlt hat (Demokratiewert unter 50). Wer Wahlen abschafft, regiert natürlich länger. Deshalb stehen diese Partien getrennt und ohne Orden.</p>}
    <Seg value={lv} options={[[0, 'Alle Stufen'], [1, 'Stufe 1'], [2, 'Stufe 2'], [3, 'Stufe 3'], [4, 'Stufe 4']]} onChange={setLv} />
    <p className="sub">Punkte zählen je Legislatur mit dem Faktor ihrer Erfahrungsstufe: Neu im Bundestag ×0,6, Erfahrene Abgeordnete ×0,8, Regierungsprofi ×1, Machtpolitik ×1,25. „Angepasst“ heißt: Ein Einfluss der Stufe war abgeschaltet, es zählt der Faktor der Stufe darunter.</p>
    <label className="sub"><input type="checkbox" checked={verifiedOnly} onChange={(e) => setVerifiedOnly(e.target.checked)} /> nur geprüfte Partien</label>
    {rows === null ? <p className="sub">Lade…</p> : !rows.length ? <p className="sub">Noch keine Einträge. Beende eine Partie mit einem angemeldeten Konto.</p> : (
      <div className="lb">{rows.map((r, i) => <div className="row" key={r.id}><span className="rank">{i + 1}</span><div className="mi"><b>{r.party_name}{r.verified && <span className="tag good" title="Vom Server nachgespielt und bestätigt" style={{ marginLeft: 8 }}>geprüft</span>}</b><span className="sub">{r.username || 'Anonym'} · {r.legislatures} {r.legislatures === 1 ? 'Legislatur' : 'Legislaturen'} · {r.laws} Gesetze · {diffLabel(r.difficulty)}</span></div><span className="num">{r.final_share != null ? de(r.final_share) + ' %' : '–'}</span><b className="num">{r.score.toLocaleString('de-DE')}</b></div>)}</div>)}
    <p className="sub">Geprüfte Partien hat der Server aus Seed und Zugprotokoll nachgespielt; die Punktzahl stammt dann nicht vom Browser.</p>
    <LiveSignupHint compact />
  </>;
}

export function Leaderboard() {
  const [tab, setTab] = usePref<'live' | 'done'>('kabinett-bestenliste-tab', 'live');
  return (
    <section className="screen">
      <div><span className="label">Bestenliste</span><h2>{tab === 'live' ? 'Laufende Regierungen' : 'Die längsten Kanzlerschaften'}</h2></div>
      {!online() ? <p className="sub">Die Bestenliste braucht eine Supabase-Verbindung.</p> : <>
        <Seg value={tab} options={[['live', 'Laufende Regierungen'], ['done', 'Beendete Partien']]} onChange={setTab} />
        {tab === 'live' ? <LiveTab /> : <DoneTab />}
      </>}
    </section>
  );
}
